import { useState } from "react";
import { Smile } from "lucide-react";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  REACTION_QUICK_SET,
  myReactionEmoji,
  normalizeReactionSummary,
  reactionTooltip,
} from "@/lib/dashboard/reactions-helpers";
import { cn } from "@/lib/utils";

/**
 * Reaction pills on an announcement's social bar, plus the quick-set picker.
 *
 * Presentational: the summary comes from the server (already carrying `mine` for this
 * caller), and every click goes back up to `onToggle`.
 *
 * The count shows even at one. These pills sit on a bar of their own rather than tucked
 * against the body text, so "who and how many" is the whole point of them — a bare emoji
 * would read as decoration.
 */
export function AnnouncementReactions({ reactions, onToggle, disabled = false }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const summary = normalizeReactionSummary(reactions);
  const mine = myReactionEmoji(summary);

  const pick = (emoji) => {
    setPickerOpen(false);
    if (!disabled) onToggle?.(emoji);
  };

  return (
    <>
      {summary.map((entry) => (
        <button
          key={entry.emoji}
          type="button"
          disabled={disabled}
          onClick={() => onToggle?.(entry.emoji)}
          title={`${reactionTooltip(entry)}${entry.mine ? " — click to remove yours" : " — click to react too"}`}
          className={cn(
            "inline-flex h-[26px] items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 text-[12px] font-bold leading-none transition-colors duration-150 disabled:cursor-default disabled:opacity-60",
            entry.mine
              ? "border-[color-mix(in_oklab,var(--primary)_45%,var(--border))] bg-[color-mix(in_oklab,var(--primary)_14%,var(--card-raised))] text-primary"
              : "border-border bg-card2 text-foreground hover:border-[color-mix(in_oklab,var(--primary)_45%,var(--border))]",
          )}
        >
          <span aria-hidden>{entry.emoji}</span>
          <span className="tabular-nums">{entry.count}</span>
        </button>
      ))}

      <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            aria-label="Add reaction"
            title="Add reaction"
            className="inline-flex h-[26px] items-center gap-1 whitespace-nowrap rounded-full border border-border bg-card2 px-2 text-[12px] font-bold leading-none text-muted-foreground transition-colors duration-150 hover:text-foreground disabled:cursor-default disabled:opacity-60"
          >
            <Smile className="h-[13px] w-[13px]" aria-hidden />+
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" sideOffset={6} className="w-auto p-1">
          <div className="flex items-center gap-0.5">
            {REACTION_QUICK_SET.map((emoji) => (
              <button
                key={emoji}
                type="button"
                onClick={() => pick(emoji)}
                aria-label={`React ${emoji}`}
                className={cn(
                  "flex h-8 w-8 items-center justify-center rounded-md text-lg leading-none transition hover:bg-muted",
                  mine === emoji && "bg-primary/15",
                )}
              >
                {emoji}
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </>
  );
}
